'use client';

import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { createContext, useContext, useMemo, useEffect, useState } from 'react';

export const ThemeContext = createContext({
  mode: 'dark',
  toggleTheme: () => {},
  accentColor: null,
  setAccentColor: () => {},
});

// Default Aiscopia Accent
const DEFAULT_ACCENT = '#38bdf8';

const themeTokens = {
  dark: {
    background: {
      default: '#1A1A1A',
      paper: '#2D2D2D',
      altAlt: '#383838',
    },
    text: {
      primary: '#F5F5F5',
      secondary: '#A0A0A0',
    },
    divider: '#444444',
  },
  light: {
    background: {
      default: '#f5f5f5',
      paper: '#ffffff',
      altAlt: '#f8f8f8',
    },
    text: {
      primary: '#1a1a1a',
      secondary: '#666666',
    },
    divider: '#e0e0e0',
  }
};

export default function ThemeProviderWrapper({ children }) {
  const [mode, setMode] = useState('dark');
  const [accentColor, setAccentColorState] = useState(DEFAULT_ACCENT);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    // Read from localStorage on mount
    const savedMode = localStorage.getItem('aiscopia_theme_mode');
    if (savedMode === 'light' || savedMode === 'dark') {
      setMode(savedMode);
    }
    const savedAccent = localStorage.getItem('aiscopia_accent_color');
    if (savedAccent) {
      setAccentColorState(savedAccent);
    }
    setMounted(true);
  }, []);

  useEffect(() => {
    if (mounted) {
      document.documentElement.setAttribute('data-theme', mode);
      localStorage.setItem('aiscopia_theme_mode', mode);
    }
  }, [mode, mounted]);

  useEffect(() => {
    if (mounted) {
      // Calculate basic contrast based on hex brightness
      let contrastText = '#ffffff';
      if (accentColor && accentColor.startsWith('#')) {
        const hex = accentColor.replace('#', '');
        if (hex.length === 6) {
          const r = parseInt(hex.substr(0, 2), 16);
          const g = parseInt(hex.substr(2, 2), 16);
          const b = parseInt(hex.substr(4, 2), 16);
          const yiq = ((r * 299) + (g * 587) + (b * 114)) / 1000;
          contrastText = (yiq >= 128) ? '#000000' : '#ffffff';
        }
      }

      // Inject the custom accent color as CSS variables directly on root
      document.documentElement.style.setProperty('--color-bg-accent-dark', accentColor);
      document.documentElement.style.setProperty('--color-bg-accent-light', accentColor);
      document.documentElement.style.setProperty('--color-fg-on-accent', contrastText);
      localStorage.setItem('aiscopia_accent_color', accentColor);
    }
  }, [accentColor, mounted]);

  const toggleTheme = () => {
    setMode((prev) => (prev === 'dark' ? 'light' : 'dark'));
  };

  const setAccentColor = (colorHex) => {
    setAccentColorState(colorHex);
  };

  const theme = useMemo(() => {
    const tokens = themeTokens[mode];

    return createTheme({
      typography: {
        fontFamily: "'Inter', sans-serif",
        fontSize: 15, // Base size
        h1: { fontSize: '30px', fontWeight: 700, lineHeight: 1.2 },
        h2: { fontSize: '24px', fontWeight: 600, lineHeight: 1.3 },
        h3: { fontSize: '20px', fontWeight: 500, lineHeight: 1.4 },
        h4: { fontSize: '16px', fontWeight: 600, lineHeight: 1.4 },
        body1: { fontSize: '15px', fontWeight: 400, lineHeight: 1.5 },
        body2: { fontSize: '13px', fontWeight: 400, lineHeight: 1.5 },
        caption: { fontSize: '12px', fontWeight: 400, lineHeight: 1.5 },
        button: { textTransform: 'none', fontWeight: 500, fontSize: '14px' },
      },
      shape: {
        borderRadius: 4,
      },
      palette: {
        mode,
        primary: {
          main: accentColor,
        },
        secondary: {
          main: '#A0A0A0',
        },
        background: tokens.background,
        text: tokens.text,
        divider: tokens.divider,
        error: { main: '#F44336' },
        warning: { main: '#FF9800' },
        success: { main: '#4CAF50' },
        info: { main: '#2196F3' },
      },
      components: {
        MuiCssBaseline: {
          styleOverrides: {
            body: {
              scrollbarColor: "var(--color-scrollbar-thumb) transparent",
              "&::-webkit-scrollbar, & *::-webkit-scrollbar": {
                width: "8px",
                height: "8px",
              },
              "&::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb": {
                borderRadius: "4px",
                backgroundColor: "var(--color-scrollbar-thumb)",
              },
              "&::-webkit-scrollbar-track, & *::-webkit-scrollbar-track": {
                borderRadius: "4px",
                backgroundColor: "transparent",
              },
            },
          },
        },
        MuiButton: {
          styleOverrides: {
            root: {
              borderRadius: '4px',
              padding: '6px 16px',
            },
            contained: {
              backgroundColor: accentColor,
              boxShadow: 'none',
              '&:hover': {
                backgroundColor: accentColor,
                filter: 'brightness(1.1)',
                boxShadow: 'none',
              },
            },
            outlined: {
              borderColor: tokens.divider,
              color: tokens.text.primary,
              '&:hover': {
                backgroundColor: tokens.background.altAlt,
                borderColor: tokens.text.secondary,
              },
            },
            text: {
              color: accentColor,
              '&:hover': {
                backgroundColor: 'rgba(56, 189, 248, 0.08)',
              },
            },
          },
        },
        MuiPaper: {
          styleOverrides: {
            root: {
              backgroundColor: tokens.background.paper,
              backgroundImage: 'none',
            },
          },
        },
        MuiAppBar: {
          styleOverrides: {
            root: {
              backgroundColor: tokens.background.paper,
              borderBottom: `1px solid ${tokens.divider}`,
              boxShadow: 'none',
            }
          }
        },
        MuiDrawer: {
          styleOverrides: {
            paper: {
              backgroundColor: tokens.background.paper,
              borderRight: `1px solid ${tokens.divider}`,
            }
          }
        },
        MuiFilledInput: {
          styleOverrides: {
            root: {
              backgroundColor: 'var(--color-bg-alt)',
              '&:hover': {
                backgroundColor: 'var(--color-bg-alt-alt)',
              },
              '&.Mui-focused': {
                backgroundColor: 'var(--color-bg-alt-alt)',
              }
            }
          }
        }
      },
    });
  }, [mode, accentColor]);

  // To prevent hydration mismatch, only render ThemeProvider when mounted
  if (!mounted) {
    return <div style={{ visibility: 'hidden' }}>{children}</div>;
  }

  return (
    <ThemeContext.Provider value={{ mode, toggleTheme, accentColor, setAccentColor }}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeContext.Provider>
  );
}